import { useCallback } from "react";
import { useAppDispatch } from "../redux/hooks";
import { openSignInPrompt } from "../redux/features/authPrompt/authPromptSlice";
import { useUser, useUserState } from "../query/UserProvider";
import { useUpdateUserStateItem } from "../query/user-state/useUpdateUserStateItem";
import { useDeleteUserStateItem } from "../query/user-state/useDeleteUserStateItem";
import { isUserStateConflictError } from "../query/user-state/userStateOptimisticQuery";
import { shouldOpenSignInPromptAfterGuestSave } from "../utils/engagement/sign-in-save-nudge-session";
import { buildSavedJobUserStatePayload } from "../utils/user-state/build-job-user-state-payload";
import { normalizeUserStateVersion } from "../utils/user-state/normalize-user-state-version";
import { captureSavedJobUserStateSyncFailure } from "../utils/user-state/capture-saved-job-user-state-sync-failure";

type SavableJob = {
  objectID?: string;
  id?: string;
  [key: string]: unknown;
};

/**
 * TEC-404: the writer for "save/unsave a job" (SRP hit cards + job detail page), synced through
 * the TanStack Query layer (`query/user-state/useUpdateUserStateItem.ts` /
 * `useDeleteUserStateItem.ts`). Phase C removed the Redux `jobs.savedJobs` dual-write — every
 * consumer now reads `savedJobs` via `extractJobsFromUserState(useUserState().data)`, so the
 * `dispatch(addSavedJob/removeSavedJob)` calls (and the `store.getState()` validation immediately
 * after each) were pure duplication of what `updateUserStateItem`/`deleteUserStateItem`'s own
 * `onMutate`/`onError`/`onSuccess` lifecycle already does — including rollback on failure, which
 * this hook used to hand-roll.
 *
 * `finlandFilter`/sign-in-prompt state (`authPromptSlice`) stays on Redux — legitimate client-only
 * UI state, not a user-state sync target.
 *
 * Conflict handling (unchanged from before Phase C): a 409 on save or unsave accepts the server's
 * winning entry wholesale — there's no locally-kept field to protect here the way
 * `useUserJobStateSync.ts`'s notes/tags/checklist edits are.
 */
export function useSavedJobActions() {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useUser();
  const { data: userState } = useUserState();
  const updateUserStateItem = useUpdateUserStateItem();
  const deleteUserStateItem = useDeleteUserStateItem();

  const saveJob = useCallback(
    async (job: SavableJob): Promise<boolean> => {
      const jobId = String(job.objectID ?? job.id ?? "");
      if (!jobId) {
        console.warn("[user-state] Tried to save job without id");
        return false;
      }

      const normalizedJob = { ...job, id: jobId };
      const savedAt = new Date().toISOString();
      const persistPayload = buildSavedJobUserStatePayload(normalizedJob, savedAt);

      const itemKey = getSavedJobItemKey(jobId);
      const currentEntry = userState?.[itemKey];
      const version = normalizeUserStateVersion(currentEntry?.version);

      try {
        await updateUserStateItem.mutateAsync({
          itemKey,
          data: persistPayload,
          version,
        });

        if (!isAuthenticated && shouldOpenSignInPromptAfterGuestSave()) {
          dispatch(openSignInPrompt());
        }
        return true; // Success
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("[user-state] Failed to persist saved job", error);
        }
        captureSavedJobUserStateSyncFailure({
          operation: "persist",
          itemKey,
          jobId,
          clientVersion: version,
          error,
        });
        // useUpdateUserStateItem's own onError already reconciled the TanStack cache — on a 409
        // that means the server's winning entry (the job is still saved), on any other failure
        // that means the pre-write entry was restored (rolled back).
        return isUserStateConflictError(error);
      }
    },
    [dispatch, updateUserStateItem, userState, isAuthenticated]
  );

  const unsaveJob = useCallback(
    async (job: SavableJob | string): Promise<boolean> => {
      const jobId = typeof job === "string" ? job : String(job.objectID ?? job.id ?? "");
      if (!jobId) {
        console.warn("[user-state] Tried to unsave job without id");
        return false;
      }

      const itemKey = getSavedJobItemKey(jobId);
      const currentEntry = userState?.[itemKey];
      const version = normalizeUserStateVersion(currentEntry?.version);

      try {
        await deleteUserStateItem.mutateAsync({
          itemKey,
          version: version > 0 ? version : undefined,
        });
        return true; // Success
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("[user-state] Failed to delete saved job", error);
        }
        captureSavedJobUserStateSyncFailure({
          operation: "delete_remote",
          itemKey,
          jobId,
          clientVersion: version,
          error,
        });
        // useDeleteUserStateItem's own onError already restored the entry on a genuine failure,
        // or wrote the server's winning entry back on a 409 (item was recreated concurrently) —
        // either way the job is still saved, so the unsave failed.
        return false;
      }
    },
    [deleteUserStateItem, userState]
  );

  return { saveJob, unsaveJob };
}

function getSavedJobItemKey(jobId: string): `savedJob#${string}` {
  return `savedJob#${jobId}`;
}
